import React, { useMemo, useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import type { MembershipInput, ProjectMembership, ProjectRole, UserSummary } from '../types';

interface MembershipAssignmentCardProps {
  users: UserSummary[];
  roles: ProjectRole[];
  memberships: ProjectMembership[];
  isSubmitting: boolean;
  onAssign: (payload: MembershipInput) => Promise<void>;
}

export const MembershipAssignmentCard: React.FC<MembershipAssignmentCardProps> = ({
  users,
  roles,
  memberships,
  isSubmitting,
  onAssign,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');

  const assignedUserIds = useMemo(() => new Set(memberships.map((membership) => membership.user.id)), [memberships]);
  const availableUsers = users.filter((user) => !assignedUserIds.has(user.id));

  const handleAssign = async () => {
    if (!selectedUserId || !selectedRoleId) return;
    await onAssign({
      user_id: Number(selectedUserId),
      role_id: Number(selectedRoleId),
    });
    setSelectedUserId('');
    setSelectedRoleId('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Project Memberships</CardTitle>
        <CardDescription>Assign users to project roles without enforcing RBAC yet.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 rounded-lg border p-4 sm:grid-cols-[1fr_1fr_auto]">
          <div className="space-y-2">
            <Label>User</Label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger>
                <SelectValue placeholder="Select user" />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.length === 0 ? (
                  <SelectItem value="__none" disabled>
                    No unassigned users
                  </SelectItem>
                ) : (
                  availableUsers.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {user.first_name} {user.last_name} ({user.email})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={String(role.id)}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={handleAssign} disabled={!selectedUserId || !selectedRoleId || isSubmitting}>
              {isSubmitting ? 'Assigning...' : 'Assign'}
            </Button>
          </div>
        </div>

        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Update Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {memberships.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                    No memberships assigned yet.
                  </TableCell>
                </TableRow>
              ) : (
                memberships.map((membership) => (
                  <TableRow key={membership.id}>
                    <TableCell className="font-medium">
                      {membership.user.first_name} {membership.user.last_name}
                    </TableCell>
                    <TableCell>{membership.user.email}</TableCell>
                    <TableCell>{membership.role.name}</TableCell>
                    <TableCell className="text-right">
                      <Select
                        value={String(membership.role.id)}
                        onValueChange={(value) =>
                          onAssign({
                            user_id: membership.user.id,
                            role_id: Number(value),
                          })
                        }
                      >
                        <SelectTrigger className="ml-auto w-44">
                          <SelectValue placeholder="Role" />
                        </SelectTrigger>
                        <SelectContent>
                          {roles.map((role) => (
                            <SelectItem key={role.id} value={String(role.id)}>
                              {role.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
